import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';
import { normalizedValue, stableKey, TtlFifoCache } from '@benzinga/utils';
import {
  StoryObject,
  News,
  BreakingNewsResponse,
  ArticleSearchResponse,
  SearchResultResponse,
  ChartbeatPost,
  NodeQueryParams,
  ChartbeatParams,
  SearchResultsParams,
} from './entities';

const DEFAULT_TTL_MS = 90000;
const FRESH_QUERY_TTL_MS = 60000;
const MAX_ENTRIES = 200;
const STORY_TTL_MS = 300000;
const TRENDING_TTL_MS = 60000;
const BREAKING_TTL_MS = 30000;

function stableCacheKey(query: SimpleNewsQuery, options: SimpleNewsOptions): string {
  return stableKey({ options: options ?? {}, query: query ?? {} });
}

function isFreshQuery(query: SimpleNewsQuery, options: SimpleNewsOptions): boolean {
  const normalizedOptions = normalizedValue(options ?? {}) as Record<string, unknown>;
  const normalizedQuery = normalizedValue(query ?? {}) as Record<string, unknown>;
  const hasDateRange = 'after' in normalizedOptions || 'before' in normalizedOptions || 'last' in normalizedOptions;
  const hasSearch = 'search' in normalizedQuery && normalizedQuery.search;
  return !hasDateRange && !hasSearch;
}

const PREFIX = {
  breaking: 'breaking:',
  chartbeat: 'chartbeat:',
  latest: 'latest:',
  nodes: 'nodes:',
  search: 'search:',
  simplyQuery: 'sq:',
  story: 'story:',
  topStoryIds: 'topIds:',
  trending: 'trending:',
} as const;

export class BasicNewsStore {
  private cache: TtlFifoCache;

  constructor(maxEntries = MAX_ENTRIES) {
    this.cache = new TtlFifoCache(maxEntries);
  }

  get(query: SimpleNewsQuery, options: SimpleNewsOptions): StoryObject[] | undefined {
    return this.cache.get<StoryObject[]>(PREFIX.simplyQuery + stableCacheKey(query, options));
  }

  set(query: SimpleNewsQuery, options: SimpleNewsOptions, stories: StoryObject[]): void {
    const ttlMs = isFreshQuery(query, options) ? FRESH_QUERY_TTL_MS : DEFAULT_TTL_MS;
    this.cache.set(PREFIX.simplyQuery + stableCacheKey(query, options), stories, ttlMs);
  }

  getTrendingNews(): News[][] | undefined {
    return this.cache.get<News[][]>(PREFIX.trending);
  }

  setTrendingNews(data: News[][]): void {
    this.cache.set(PREFIX.trending, data, TRENDING_TTL_MS);
  }

  getTopStoryIds(): number[] | undefined {
    return this.cache.get<number[]>(PREFIX.topStoryIds);
  }

  setTopStoryIds(data: number[]): void {
    this.cache.set(PREFIX.topStoryIds, data, TRENDING_TTL_MS);
  }

  getStory(id: number): News | undefined {
    return this.cache.get<News>(PREFIX.story + id);
  }

  setStory(id: number, data: News): void {
    this.cache.set(PREFIX.story + id, data, STORY_TTL_MS);
  }

  getFetchNodes(params: NodeQueryParams): News[] | undefined {
    return this.cache.get<News[]>(PREFIX.nodes + stableKey(params as Record<string, unknown>));
  }

  setFetchNodes(params: NodeQueryParams, data: News[]): void {
    this.cache.set(PREFIX.nodes + stableKey(params as Record<string, unknown>), data, DEFAULT_TTL_MS);
  }

  getLatestArticles(query: string): ArticleSearchResponse | undefined {
    return this.cache.get<ArticleSearchResponse>(PREFIX.latest + query);
  }

  setLatestArticles(query: string, data: ArticleSearchResponse): void {
    this.cache.set(PREFIX.latest + query, data, DEFAULT_TTL_MS);
  }

  getSearchResults(params: SearchResultsParams): SearchResultResponse | undefined {
    return this.cache.get<SearchResultResponse>(
      PREFIX.search + stableKey(params as unknown as Record<string, unknown>),
    );
  }

  setSearchResults(params: SearchResultsParams, data: SearchResultResponse): void {
    this.cache.set(PREFIX.search + stableKey(params as unknown as Record<string, unknown>), data, DEFAULT_TTL_MS);
  }

  getChartbeat(params: ChartbeatParams): { pages: ChartbeatPost[] } | undefined {
    return this.cache.get<{ pages: ChartbeatPost[] }>(PREFIX.chartbeat + stableKey(params as Record<string, unknown>));
  }

  setChartbeat(params: ChartbeatParams, data: { pages: ChartbeatPost[] }): void {
    this.cache.set(PREFIX.chartbeat + stableKey(params as Record<string, unknown>), data, TRENDING_TTL_MS);
  }

  getBreakingNews(): BreakingNewsResponse | undefined {
    return this.cache.get<BreakingNewsResponse>(PREFIX.breaking);
  }

  setBreakingNews(data: BreakingNewsResponse): void {
    this.cache.set(PREFIX.breaking, data, BREAKING_TTL_MS);
  }
}
